import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Copy,
  KeyRound,
  Loader2,
  Plus,
  RotateCw,
  Trash2,
  CheckCircle2,
  TerminalSquare,
  History,
} from "lucide-react";
import { ApiKeyTester, ENDPOINTS_BY_ROLE } from "../components/ApiKeyTester";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiKeysApi,
  ApiError,
  type ApiKey,
  type ApiKeyRoleEntry,
  type ApiKeyScope,
  type MintApiKeyResponse,
} from "@/lib/api";

// Friendly display labels for the agent role codes returned by the catalog
// endpoint. Falls through to the role_code string when a role isn't listed
// here (e.g. a brand-new role added server-side before the frontend
// catches up).
const ROLE_LABELS: Record<string, string> = {
  "cs.ticket_responder": "Customer Support — Ticket Responder",
  "cs.call_center": "Customer Support — Call Center",
  "ea.general": "Executive Assistant",
  "engineer.backend": "Engineer — Backend",
  "engineer.frontend": "Engineer — Frontend",
  "engineer.database": "Engineer — Database",
  "engineer.infrastructure": "Engineer — Infrastructure",
  "analytics.bi_dev": "Analytics — BI Developer",
  "analytics.data": "Analytics — Data Analyst",
  "bi.developer": "Analytics — BI Developer",
  "data.analyst": "Analytics — Data Analyst",
  "growth.sdr": "Growth — SDR",
  "growth.ads": "Growth — Ad Campaigns",
  "growth.ad_campaign": "Growth — Ad Campaigns",
  "growth.seo_strategist": "Growth — SEO Strategist",
  "growth.social": "Growth — Social Media",
};

function roleLabel(code: string): string {
  return ROLE_LABELS[code] ?? code;
}

// Per-scope tooltip — kept terse; the OpenAPI doc carries the full surface.
const SCOPE_DESCRIPTIONS: Record<string, string> = {
  "tickets:read": "List and read tickets via GET /tickets.",
  "tickets:write": "Create + update tickets.",
  "tickets:close": "Close tickets via /tickets/:id/close.",
  "calls:read": "Read call records.",
  "calls:answer": "Answer inbound voice calls.",
  "emails:read": "Read inbox messages.",
  "emails:send": "Send mail through the EA.",
  "calendar:read": "Read calendar events.",
  "calendar:write": "Create + update calendar events.",
  "repos:read": "Read repository contents.",
  "prs:read": "Read pull requests.",
  "prs:write": "Open + update pull requests.",
  "runs:read": "Read CI / job-run status.",
  "queries:run": "Execute SQL / dashboard queries.",
  "dashboards:publish": "Publish or update dashboards.",
  "metrics:read": "Read metrics + KPIs.",
  "leads:read": "Read CRM leads.",
  "leads:write": "Create + update CRM leads.",
  "contacts:write": "Create + update CRM contacts.",
  "campaigns:read": "Read marketing campaigns.",
  "campaigns:write": "Create + update marketing campaigns.",
  "content:write": "Publish content (blog, SEO).",
  "posts:read": "Read social-media posts.",
  "posts:write": "Schedule + publish social posts.",
};

export function TenantApiKeys({ tenantCode }: { tenantCode: string }) {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState("");
  const [roleCode, setRoleCode] = useState<string>("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [revealed, setRevealed] = useState<MintApiKeyResponse | null>(null);
  // Tester state — when non-null the tester dialog is open. roleCode +
  // keyLabel + initialSecret pre-populate the tester so a freshly minted
  // key auto-fills (the secret never leaves memory).
  const [tester, setTester] = useState<{
    roleCode: string;
    roleLabel: string;
    keyLabel: string;
    initialSecret?: string;
  } | null>(null);

  // Catalog lookup — drives both the role dropdown and the scope checkbox
  // list in the mint form. Cached forever (the catalog is global + small).
  const catalog = useQuery<ApiKeyRoleEntry[], ApiError>({
    queryKey: ["api-keys-catalog", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await apiKeysApi.catalog(tenantCode, idToken);
      return res.roles;
    },
    enabled: !!idToken,
    staleTime: Infinity,
  });

  const list = useQuery<ApiKey[], ApiError>({
    queryKey: ["api-keys", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await apiKeysApi.list(tenantCode, idToken);
      return res.api_keys;
    },
    enabled: !!idToken,
  });

  // Pre-select first role + its scopes once the catalog lands. Keeps the
  // mint form usable without a manual click.
  const sortedCatalog = useMemo(
    () =>
      [...(catalog.data ?? [])].sort((a, b) =>
        roleLabel(a.role_code).localeCompare(roleLabel(b.role_code)),
      ),
    [catalog.data],
  );
  useEffect(() => {
    // Pre-select a role + reset scopes once the catalog lands. Prefer the
    // first role with a wired-up public endpoint so "Test key" works out of
    // the box; otherwise fall back to the first role alphabetically.
    if (!roleCode && sortedCatalog.length > 0) {
      const firstWithEndpoint = sortedCatalog.find(
        (r) => (ENDPOINTS_BY_ROLE[r.role_code] ?? []).length > 0,
      );
      setRoleCode((firstWithEndpoint ?? sortedCatalog[0]).role_code);
      setScopes([]);
    }
  }, [roleCode, sortedCatalog]);

  const selectedRoleHasEndpoint =
    !!roleCode && (ENDPOINTS_BY_ROLE[roleCode] ?? []).length > 0;

  const selectedRoleEntry = sortedCatalog.find((r) => r.role_code === roleCode);
  const availableScopes = selectedRoleEntry?.scopes ?? [];

  const mintMutation = useMutation<MintApiKeyResponse, ApiError>({
    mutationFn: async () => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      return apiKeysApi.mint(
        tenantCode,
        { label: label.trim(), role_code: roleCode, scopes },
        idToken,
      );
    },
    onSuccess: (res) => {
      setRevealed(res);
      setLabel("");
      setScopes([]);
      queryClient.invalidateQueries({ queryKey: ["api-keys", tenantCode] });
      toast.success("API key created");
    },
    onError: (err) => {
      console.error("mint", err);
      toast.error("Couldn't mint key", { description: err.message });
    },
  });

  const revokeMutation = useMutation<{ revoked: true }, ApiError, ApiKey>({
    mutationFn: async (key) => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      return apiKeysApi.revoke(tenantCode, key.id, idToken);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys", tenantCode] });
      queryClient.invalidateQueries({ queryKey: ["api-keys-history", tenantCode] });
      toast.success("Key revoked");
    },
    onError: (err) => {
      console.error("revoke", err);
      toast.error("Couldn't revoke", { description: err.message });
    },
  });

  // Rotate revokes the existing key and mints a fresh one with the same
  // role + scopes. The new secret comes back in `revealed` so the
  // existing copy/paste card surfaces it (one consistent UX with mint).
  const rotateMutation = useMutation<MintApiKeyResponse, ApiError, ApiKey>({
    mutationFn: async (key) => {
      if (!idToken) throw new ApiError("no_token", "Not authenticated", 401);
      return apiKeysApi.rotate(tenantCode, key.id, idToken);
    },
    onSuccess: (res) => {
      setRevealed(res);
      queryClient.invalidateQueries({ queryKey: ["api-keys", tenantCode] });
      queryClient.invalidateQueries({ queryKey: ["api-keys-history", tenantCode] });
      toast.success("Key rotated — copy the new secret now");
    },
    onError: (err) => {
      console.error("rotate", err);
      toast.error("Couldn't rotate", { description: err.message });
    },
  });

  const history = useQuery<ApiKey[], ApiError>({
    queryKey: ["api-keys-history", tenantCode, idToken],
    queryFn: async () => {
      if (!idToken) return [];
      const res = await apiKeysApi.history(tenantCode, idToken);
      return res.api_keys;
    },
    enabled: !!idToken,
  });

  const toggleScope = (s: ApiKeyScope) => {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const copySecret = async () => {
    if (!revealed?.secret) return;
    try {
      await navigator.clipboard.writeText(revealed.secret);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy — copy manually");
    }
  };

  const canMint =
    label.trim().length > 0 &&
    !!roleCode &&
    scopes.length > 0 &&
    !mintMutation.isPending;

  // Group active keys by role so each role gets its own card. Keys without a
  // role_code (shouldn't happen post-migration, but defensive) land in a
  // generic bucket.
  const keysByRole = useMemo(() => {
    const groups = new Map<string, ApiKey[]>();
    for (const k of list.data ?? []) {
      const code = k.role_code || "(unscoped)";
      const arr = groups.get(code) ?? [];
      arr.push(k);
      groups.set(code, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) =>
      roleLabel(a).localeCompare(roleLabel(b)),
    );
  }, [list.data]);

  return (
    <div className="p-8 space-y-8">
      <header>
        <h1 className="font-display text-4xl">API Keys</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          One key per role per integration — used by your systems to push data
          into VirtualDesk via{" "}
          <span className="font-mono text-foreground">X-VirtualDesk-API-Key</span>.
          Disabling a key only takes that role's external integration offline.
        </p>
      </header>

      {revealed && (
        <Card className="border-signal-ok/40 bg-signal-ok/5">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 text-signal-ok">
              <CheckCircle2 className="h-4 w-4" />
              <p className="text-sm font-medium">Key created — copy it now</p>
            </div>
            <p className="text-xs text-muted-foreground">
              This is the only time you'll see the full key. Store it somewhere safe; if you lose
              it you'll need to mint a new one.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md border border-border bg-brand-midnight/60 px-3 py-2 font-mono text-xs break-all">
                {revealed.secret}
              </code>
              <Button variant="outline" size="sm" onClick={copySecret}>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setTester({
                    roleCode: revealed.api_key.role_code,
                    roleLabel: roleLabel(revealed.api_key.role_code),
                    keyLabel: revealed.api_key.label,
                    initialSecret: revealed.secret,
                  })
                }
              >
                <TerminalSquare className="h-3.5 w-3.5" />
                Test now
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setRevealed(null)}>
                I've stored it
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-brand-violet" />
            <p className="text-sm font-medium">New API key</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="key-role">Agent role</Label>
              <Select
                value={roleCode}
                onValueChange={(v) => {
                  setRoleCode(v);
                  setScopes([]); // role change resets scopes
                }}
              >
                <SelectTrigger id="key-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {sortedCatalog.map((r) => {
                    const ready = (ENDPOINTS_BY_ROLE[r.role_code] ?? []).length > 0;
                    return (
                      <SelectItem
                        key={r.role_code}
                        value={r.role_code}
                        disabled={!ready}
                      >
                        {roleLabel(r.role_code)}
                        {ready ? null : (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                            coming soon
                          </span>
                        )}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                One key, one role. Pick the agent surface this key will hit —
                disabling later takes only that role's integration offline.
              </p>
              {roleCode && !selectedRoleHasEndpoint ? (
                <p className="text-[11px] text-signal-awaiting">
                  No public endpoint is wired up for this role yet — you can mint
                  a key, but Test key will report nothing to call until the role
                  ships its public surface.
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="key-label">Label</Label>
              <Input
                id="key-label"
                placeholder="e.g. fitfab-prod-cs"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Just for your bookkeeping — name it after the system that'll use it.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Scopes</Label>
            {availableScopes.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {catalog.isLoading ? "Loading…" : "Pick a role above to see its scopes."}
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {availableScopes.map((s) => (
                  <label
                    key={s}
                    className="flex items-start gap-2 rounded-md border border-border p-2 cursor-pointer hover:bg-muted/30"
                  >
                    <input
                      type="checkbox"
                      checked={scopes.includes(s)}
                      onChange={() => toggleScope(s)}
                      className="mt-1 accent-brand-violet"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs">{s}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {SCOPE_DESCRIPTIONS[s] ?? "—"}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <Button onClick={() => mintMutation.mutate()} disabled={!canMint}>
              {mintMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create key
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <p className="text-sm font-medium text-muted-foreground">Active keys by role</p>
        {list.isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : keysByRole.length > 0 ? (
          keysByRole.map(([code, keys]) => (
            <RoleSection
              key={code}
              roleCode={code}
              keys={keys}
              onRevoke={(k) => revokeMutation.mutate(k)}
              onRotate={(k) => rotateMutation.mutate(k)}
              onTest={(k) =>
                setTester({
                  roleCode: k.role_code,
                  roleLabel: roleLabel(k.role_code),
                  keyLabel: k.label,
                })
              }
              revokingId={
                revokeMutation.isPending && revokeMutation.variables
                  ? revokeMutation.variables.id
                  : null
              }
              rotatingId={
                rotateMutation.isPending && rotateMutation.variables
                  ? rotateMutation.variables.id
                  : null
              }
            />
          ))
        ) : (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
            No keys yet.
          </div>
        )}
      </div>

      <KeyHistory rows={history.data ?? []} loading={history.isLoading} />

      {tester ? (
        <ApiKeyTester
          open
          onOpenChange={(next) => !next && setTester(null)}
          roleCode={tester.roleCode}
          roleLabel={tester.roleLabel}
          keyLabel={tester.keyLabel}
          initialSecret={tester.initialSecret}
        />
      ) : null}
    </div>
  );
}

function RoleSection({
  roleCode,
  keys,
  onRevoke,
  onRotate,
  onTest,
  revokingId,
  rotatingId,
}: {
  roleCode: string;
  keys: ApiKey[];
  onRevoke: (k: ApiKey) => void;
  onRotate: (k: ApiKey) => void;
  onTest: (k: ApiKey) => void;
  revokingId: string | null;
  rotatingId: string | null;
}) {
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">{roleLabel(roleCode)}</p>
          <span className="font-mono text-[11px] text-muted-foreground">
            {keys.length} key{keys.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="space-y-2">
          {keys.map((k) => (
            <KeyRow
              key={k.id}
              apiKey={k}
              onRevoke={() => onRevoke(k)}
              onRotate={() => onRotate(k)}
              onTest={() => onTest(k)}
              revoking={revokingId === k.id}
              rotating={rotatingId === k.id}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// KeyHistory renders the revoked-key audit list at the bottom of /api-keys.
// Each row carries the role + label + (started at, ended at) window so an
// operator can trace which credential was live when. Clicking nothing —
// revoked keys are read-only.
function KeyHistory({ rows, loading }: { rows: ApiKey[]; loading: boolean }) {
  if (loading && rows.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <History className="h-4 w-4" />
        <span>Rotation history</span>
        <span className="font-mono text-[11px]">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 p-4 text-center text-[11px] text-muted-foreground">
          No revoked keys yet — rotated or revoked credentials show up here.
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/30 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Role</th>
                  <th className="text-left px-4 py-2 font-medium">Label</th>
                  <th className="text-left px-4 py-2 font-medium">Prefix</th>
                  <th className="text-left px-4 py-2 font-medium">Live from</th>
                  <th className="text-left px-4 py-2 font-medium">Revoked</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.id} className="border-t border-border/40">
                    <td className="px-4 py-2 text-muted-foreground">
                      {roleLabel(k.role_code)}
                    </td>
                    <td className="px-4 py-2 truncate max-w-[18rem]">{k.label}</td>
                    <td className="px-4 py-2 font-mono">{k.key_prefix}…</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {new Date(k.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {k.revoked_at ? new Date(k.revoked_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KeyRow({
  apiKey,
  onRevoke,
  onRotate,
  onTest,
  revoking,
  rotating,
}: {
  apiKey: ApiKey;
  onRevoke: () => void;
  onRotate: () => void;
  onTest: () => void;
  revoking: boolean;
  rotating: boolean;
}) {
  return (
    <div className="rounded-md border border-border bg-muted/10 p-3 flex items-start gap-3 flex-wrap">
      <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet shrink-0">
        <KeyRound className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{apiKey.label}</p>
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">
          {apiKey.key_prefix}…
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {apiKey.scopes.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-mono text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Created {formatDistanceToNow(new Date(apiKey.created_at), { addSuffix: true })}
          {apiKey.last_used_at && (
            <>
              {" · "}last used{" "}
              {formatDistanceToNow(new Date(apiKey.last_used_at), { addSuffix: true })}
            </>
          )}
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onTest}
        title="Run a request against this endpoint with your API key"
      >
        <TerminalSquare className="h-3.5 w-3.5" />
        Test
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onRotate}
        disabled={rotating || revoking}
        title="Revoke this key and mint a fresh one with the same role + scopes"
      >
        {rotating ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <>
            <RotateCw className="h-3.5 w-3.5" />
            Rotate
          </>
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRevoke}
        disabled={revoking || rotating}
        className="text-signal-failed hover:text-signal-failed hover:bg-signal-failed/10"
      >
        {revoking ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <>
            <Trash2 className="h-3.5 w-3.5" />
            Revoke
          </>
        )}
      </Button>
    </div>
  );
}
